import { gql, TypedDocumentNode } from '@apollo/client';
import { CommercialControlSlice, EngineeringControlSlice, ProjectManagementControlSlice } from '../types';

type ControlSliceVariables = { id: string };

export type ControlSliceQuery<TSlice> = TypedDocumentNode<{ baseProject: TSlice }, ControlSliceVariables>;

const PROJECT_DIMENSION_FIELDS = `
  projectDimension {
    engagementType
    commercialState
  }
`;

const FORECAST_FIELDS = `
  forecast {
    id
    projectId
    plannedProgress
    actualProgress
    forecastCompletionBudgetHours
    forecastCompletionDate
  }
`;

const ENGINEERING_POLICY_FIELDS = `
  engineeringPolicy {
    id
    projectId
    productionOwnership
    deliveryModel
    ppcLink
    ppcLinkUpdatedAt
    updatedAt
    createdAt
  }
`;

const HEALTH_FIELDS = `
  health {
    id
    projectId
    zone
    healthChecks {
      id
      healthId
      name
      status
      message
      targetZone
      zoneSince
      createdAt
      latestEntityModificationDate
    }
    createdAt
    updatedAt
  }
`;

export const GET_CONTROL_TAB_DATA = gql`
  query GetCommercialControlData($id: ID!) {
    baseProject(id: $id) {
      id
      legacyId
      projectManager
      budget {
        id
        billingType
        fte
        endDate
        extendedEndDate
        structure {
          id
          hours
          source
          status
        }
      }
      ${FORECAST_FIELDS}
      settings {
        reportSettings {
          reportFrequency
          isSystemReportUsed
          approvalType
        }
        cycleActivitySettings {
          cycleFrequency
          cycleStartDay
          cycleEndDay
          planningDay
          demoDay
          cycleSettingsStatus
          cycleChangeReason
          cycleRejectionReason
        }
        forecastSettings {
          isProjectForecastTrackingEnabled
          isEvmTrackingEnabled
        }
      }
      ${PROJECT_DIMENSION_FIELDS}
      accountData {
        sowId
        accountName
        accountId
        isAccountCodeName
        ownerAlias
        isPortalEnabled
      }
      team {
        id
        members {
          employeeAlias
          role
          fte
          isBench
          isBackup
        }
      }
      ${HEALTH_FIELDS}
      staffingRisk {
        id
        projectId
        priority
        issues {
          id
          description
          status
          targetPriority
          systemIssueType
          staffingRiskId
          createdAt
          updatedAt
        }
      }
      milestones {
        id
        alias
        title
        type
        status
        description
        rejectionReason
        businessOutcomes
        initialPlannedDate
        currentPlannedDate
        completionDate
        rejectionDate
        comment
      }
      risks {
        id
        alias
        title
        status
        probability
        severity
        type
        isEscalated
        severity
        totalScore
        projectId
        riskOwner
        createdAt
        updatedAt
      }
      assumptions {
        id
        projectId
        number
        description
        assignedRole
        isVerificationRequired
        status
      }
      engineeringIncidents {
        id
        projectId
        shortDescription
        severity
        linkToDetails
        notes
        updatedAt
        createdAt
      }
      engineeringBugs {
        id
        projectId
        shortDescription
        isCritical
        linkToDetails
        notes
        createdAt
        updatedAt
      }
      ${ENGINEERING_POLICY_FIELDS}
    }
  }
`;

export const GET_COMMERCIAL_CONTROL_DATA: ControlSliceQuery<CommercialControlSlice> = gql`
  query GetCommercialControlSlice($id: ID!) {
    baseProject(id: $id) {
      id
      ${PROJECT_DIMENSION_FIELDS}
      ${HEALTH_FIELDS}
      budget {
        id
        endDate
        extendedEndDate
      }
    }
  }
`;

export const GET_PROJECT_MANAGEMENT_CONTROL_DATA: ControlSliceQuery<ProjectManagementControlSlice> = gql`
  query GetProjectManagementControlSlice($id: ID!) {
    baseProject(id: $id) {
      id
      ${FORECAST_FIELDS}
      ${HEALTH_FIELDS}
    }
  }
`;

export const GET_ENGINEERING_CONTROL_DATA: ControlSliceQuery<EngineeringControlSlice> = gql`
  query GetEngineeringControlSlice($id: ID!) {
    baseProject(id: $id) {
      id
      ${ENGINEERING_POLICY_FIELDS}
      ${HEALTH_FIELDS}
    }
  }
`;
